/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @next/next/no-html-link-for-pages */
/* eslint-disable @next/next/no-img-element */
import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { GetServerSideProps } from "next";
import Lightbox from "react-image-lightbox";
import { CartContext } from "context/cart";
import { LogContext } from "context/log";
import { ModalContext } from "context/modal";
import { Button } from "@pepdirect/ui/button";
import { logProductDetailBuyButtonClickEvent } from "@pepdirect/helpers/analyticsLogger";
import { convertFromCentsToDollars } from "helpers/currency";
import { PAGE_TYPE_ENUM, Product } from "@pepdirect/shared/types";
import { Subscription_Options } from "constants/subscription";
import { useItemByGtinQuery } from "services/graphql/generated";
import RockstarHeader from "components/RockstarHeader";
import RockstarFooter from "components/RockstarFooter";
import { PriceChooser } from "components/PriceChooser";
import { AppContext } from "context/app";
import { DatoProductFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import { StructuredText } from "react-datocms";
import DatoHeadTagsRenderer from "components/DatoHeadTagsRenderer";
import RockstarRelatedProducts from "components/RockstarRelatedProducts";
import TopCtaBanner from "components/TopCtaBanner";
import Slider, { Settings } from "react-slick";
import Link from "next/link";

const { ONE_TIME, SUB_SAVE } = Subscription_Options;
// const { maxCartQty: maxCartQtyDefault } = cart;

interface ProductPageProps {
  product?: DatoProductFragment | null;
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async (
  context
) => {
  const slug = context?.params?.slug;

  try {
    const { product } = await getSdk(
      createDatoClient(context.preview)
    ).ProductBySlug({ slug: `${slug}` });

    if (!product) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        product: product,
      },
    };
  } catch (error) {
    return {
      props: {
        product: null,
      },
    };
  }
};

// const NA = "N/A";

const ProductPage: FC<ProductPageProps> = ({ product }) => {
  const viewportSlider = useRef<Slider>(null);
  const thumbnailSlider = useRef<Slider>(null);
  const { useLogPageView } = useContext(LogContext);
  const { tenant } = useContext(AppContext);
  const { cart, updateCartItem } = useContext(CartContext);
  const { open } = useContext(ModalContext);

  const [currentSelection, setCurrentSelection] = useState<number>(1);
  const [showNotAvailableModal, setShowNotAvailableModal] =
    useState<boolean>(false);
  const [lightboxImage, setLightboxImage] = useState<{
    activeIdx: number;
    isOpen: boolean;
  }>({ activeIdx: 0, isOpen: false });
  const { data: pepDirectProductData } = useItemByGtinQuery({
    variables: { gtin: product?.gtin || "" },
  });

  const pepDirectProduct = pepDirectProductData?.itemByGtin;
  const cartItem = cart?.items?.find(
    (item) => item.product.id === pepDirectProduct?._id
  );
  const quantity = cartItem?.quantity || 0;
  const isInCart = quantity > 0;

  const [allProducts, setAllProducts] = useState<DatoProductFragment[]>([]);
  const router = useRouter();

  useEffect(() => {
    getSdk(createDatoClient(router?.isPreview))
      .products()
      .then(({ allProducts }) => {
        setAllProducts(allProducts);
      });
  }, [router?.isPreview]);

  const [subscriptionOption, setSubscriptionOption] =
    useState<Subscription_Options>(
      cartItem?.subscriptionIntervalInDays ? SUB_SAVE : ONE_TIME
    );

  const { discount, isSubscribable, subscriptionPrice } =
    pepDirectProduct?.subscriptionMetadata || {};
  const price = convertFromCentsToDollars(pepDirectProduct?.itemPrice || 0);
  const formattedSubscriptionPrice =
    (subscriptionPrice && convertFromCentsToDollars(subscriptionPrice)) || null;
  const intervals = useMemo(() => {
    const intervalsVal =
      tenant?.shoppingBagConfig?.availableSubscriptionIntervalsInDays || [];
    return intervalsVal;
  }, [tenant]);
  const showSubscriptions =
    isSubscribable && tenant?.shoppingBagConfig?.orderSubscriptionsEnabled;

  const [defaultInterval] = intervals;

  const [interval, setInterval] = useState(
    cartItem?.subscriptionIntervalInDays || defaultInterval
  );

  useEffect(() => {
    const [firstInterval] = intervals;
    setInterval(cartItem?.subscriptionIntervalInDays || firstInterval);
  }, [cartItem?.subscriptionIntervalInDays, intervals]);

  useEffect(() => {
    if (cartItem?.subscriptionIntervalInDays) {
      setInterval(cartItem?.subscriptionIntervalInDays);
      setSubscriptionOption(SUB_SAVE);
    }
  }, [cartItem?.subscriptionIntervalInDays]);

  const buttonLabel =
    subscriptionOption === "ONE_TIME"
      ? isInCart
        ? `Update Cart`
        : `Add To Cart`
      : `SUBSCRIBE`;
  const logProduct = useMemo(
    () => ({
      _id: product?.id || "",
      gtin: product?.gtin || "",
      itemTitle: product?.title || "",
      itemPrice: pepDirectProduct?.price || null,
      itemDesignerArtistBrandName:
        pepDirectProduct?.itemDesignerArtistBrandName || null,
      itemDimensions: pepDirectProduct?.itemDimensions || null,
    }),
    // Don't need to handle this because the product is already coming from server side
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useLogPageView(
    {
      pageType: PAGE_TYPE_ENUM.product,
      pageCategory: product?.flavorName || "",
      pageTitle: logProduct.itemTitle,
      id: logProduct._id,
    },
    logProduct as Product
  );

  const images = product?.productImages?.map((image) => image?.url);

  const priceItem = {
    basePrice: pepDirectProduct?.price || null,
    discountAmount: discount?.amount || 0,
    id: product?.gtin || "",
    item: {
      imgUrl: images?.[0],
      price: pepDirectProduct?.price || null,
      subscriptionMetadata: {
        isSubscribable: isSubscribable,
        subscriptionPrice: subscriptionPrice,
      },
      title: product?.title || "",
    },
    quantity: currentSelection,
    subscriptionIntervalInDays: subscriptionOption === SUB_SAVE && interval,
  };

  const handleAddItemsToCart = async () => {
    logProductDetailBuyButtonClickEvent(
      "",
      {
        _id: pepDirectProduct?.id || "",
        gtin: pepDirectProduct?.gtin || "",
        itemTitle: product?.title || "",
        itemPrice: pepDirectProduct?.price || null,
        itemDesignerArtistBrandName:
          pepDirectProduct?.itemDesignerArtistBrandName || null,
        itemDimensions: pepDirectProduct?.itemDimensions || null,
      } as unknown as Product,
      quantity
    );
    await updateCartItem({
      productId: pepDirectProduct?._id,
      quantity: currentSelection,
      subscriptionIntervalInDays:
        subscriptionOption === SUB_SAVE && interval ? interval : null,
    });
    open("UPSELL_MODAL", {
      priceItem: priceItem,
      products: allProducts,
    });
  };

  useEffect(() => {
    if (quantity > 0) {
      setCurrentSelection(quantity);
    }
  }, [quantity]);

  const isOutOfStock =
    !!pepDirectProduct?.isOutOfStock ||
    (pepDirectProduct?.inventoryQty || 0) === 0;

  const storeLocatorLink =
    product?.storeLocator ||
    "https://destinilocators.com/pepsiconew/site/locator.php?TLL=6443";

  const viewportSliderSettings: Settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    fade: true,
    infinite: false,
    swipe: false,
    asNavFor: thumbnailSlider?.current as Slider,
    responsive: [
      {
        breakpoint: 767,
        settings: {
          swipe: true,
        },
      },
    ],
  };

  const thumbnailSliderSettings: Settings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    asNavFor: viewportSlider?.current as Slider,
    dots: false,
    centerMode: false,
    vertical: true,
    verticalSwiping: false,
    infinite: false,
    arrows: false,
    swipe: false,
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          vertical: false,
          verticalSwiping: false,
        },
      },
    ],
  };

  return (
    <>
      {product?.seoTagsGenerator && (
        <DatoHeadTagsRenderer datoSeoTag={product?.seoTagsGenerator[0]} />
      )}

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              <article
                id="post-957"
                className="post-957 products type-products status-publish hentry entry"
              >
                <div
                  className="product-content-area"
                  style={{ minHeight: "650px" }}
                >
                  <div className="product-main">
                    <div className="rockstar-product">
                      <div
                        className="rockstar-product-gallery"
                        style={{ maxHeight: "650px" }}
                      >
                        <Slider
                          {...viewportSliderSettings}
                          ref={viewportSlider}
                          className="rockstar-product-viewport"
                        >
                          {images?.map((image, imageIdx) => (
                            <a
                              key={image}
                              href={image}
                              data-fancybox="gallery"
                              title=""
                              onClick={async (e) => {
                                e.preventDefault();
                                setLightboxImage({
                                  isOpen: true,
                                  activeIdx: imageIdx,
                                });
                              }}
                            >
                              <img src={image} alt={product?.title || ""} />
                            </a>
                          ))}
                        </Slider>
                        <Slider
                          {...thumbnailSliderSettings}
                          ref={thumbnailSlider}
                          className="rockstar-product-thumb"
                        >
                          {images?.map((image) => (
                            <div key={image} className="product-thumb-img">
                              <img
                                src={image}
                                alt=""
                                height="114"
                                width="auto"
                              />
                            </div>
                          ))}
                        </Slider>
                      </div>
                      <Link
                        href="/shop-all"
                        title="Back to all"
                        className="pdp-back-btn"
                      >
                        Back to all
                      </Link>
                      <div className="rockstar-product-summary">
                        <h1 className="product-title entry-title">
                          {product?.title || ""}
                        </h1>

                        <div className="product-variations one-variation">
                          <select
                            className="product-variation-select select2-hidden-accessible"
                            name=""
                            data-select2-id="select2-data-1-12go"
                            tabIndex={-1}
                            aria-hidden="true"
                          >
                            <option
                              value={product?.productVariant || ""}
                              data-select2-id="select2-data-3-09wm"
                            >
                              {product?.productVariant || ""}
                            </option>
                          </select>
                        </div>
                        <div className="price product-price">
                          {pepDirectProduct &&
                          subscriptionOption === "SUB_SAVE" ? (
                            <>
                              <span>{formattedSubscriptionPrice || price}</span>
                              {subscriptionPrice && (
                                <span className="originalPrice">{price}</span>
                              )}
                            </>
                          ) : (
                            !isOutOfStock && (
                              <>
                                <span>{price}</span>
                              </>
                            )
                          )}
                        </div>
                        {pepDirectProduct && !isOutOfStock ? (
                          <form className="product-form" style={{ opacity: 1 }}>
                            <div className="product-more-info product-quantity-block product-quantity-field">
                              <label className="product-quantity-text">
                                QUANTITY
                              </label>
                              <div className="quantity-field">
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (currentSelection > 0) {
                                      setCurrentSelection((prev) => prev - 1);
                                    }
                                  }}
                                  className="min-btn"
                                >
                                  -
                                </a>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (
                                      currentSelection <
                                      (tenant?.shoppingBagConfig
                                        ?.maxAmountForSelect || 15)
                                    ) {
                                      setCurrentSelection((prev) => prev + 1);
                                    }
                                  }}
                                  className="plus-btn"
                                >
                                  +
                                </a>
                                <input
                                  type="number"
                                  className="input-text qty text"
                                  step="1"
                                  min="1"
                                  max="100"
                                  name="quantity"
                                  title="Qty"
                                  size={4}
                                  placeholder=""
                                  inputMode="numeric"
                                  value={currentSelection}
                                  onChange={() => {}}
                                />
                              </div>
                              {product?.nutritionLink && (
                                <a
                                  href={product?.nutritionLink}
                                  className="product-nutrition"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  NUTRITION
                                </a>
                              )}
                            </div>
                            {showSubscriptions && interval && (
                              <>
                                <PriceChooser
                                  discount={{
                                    id: discount?.id,
                                    activeFrom: discount?.activeFrom,
                                    amount: discount?.amount || 0,
                                    isPercent: discount?.isPercent || false,
                                  }}
                                  intervals={intervals}
                                  onIntervalChange={setInterval}
                                  onSubscriptionOptionChange={
                                    setSubscriptionOption
                                  }
                                  price={price}
                                  selectedInterval={interval}
                                  subscriptionOption={subscriptionOption}
                                  subscriptionPrice={formattedSubscriptionPrice}
                                />
                                {subscriptionOption === "SUB_SAVE" && (
                                  <p className="product-subscription-disclosure">
                                    <span>Subscription Sign Up Disclosure</span>
                                    <br />
                                    By clicking Subscribe, I agree to receive my
                                    products based on my selected delivery
                                    schedule and understand my provided payment
                                    method will be charged on a recurring basis.
                                    Charges and frequency may vary. I may cancel
                                    at any time. See our{" "}
                                    <a
                                      href="https://contact.pepsico.com/rockstarenergy/article/subscription-terms-and-conditions"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Subscription Terms
                                    </a>{" "}
                                    for more information.
                                  </p>
                                )}
                              </>
                            )}
                            <div className="product-buttons">
                              <Button
                                onClick={handleAddItemsToCart}
                                className="add-to-cart button alt"
                                style={{ height: "57px" }}
                              >
                                {buttonLabel}
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <div className="product-buttons">
                            <p className="not-available">
                              This item is currently unavailable.
                            </p>
                            <Button
                              className="find-in-store button alt"
                              onClick={() => setShowNotAvailableModal(true)}
                              style={{ height: "57px" }}
                            >
                              See buying options
                            </Button>
                          </div>
                        )}
                        <div className="product-short-description">
                          <StructuredText
                            data={product?.productContent?.value}
                          />
                        </div>
                        <ul className="product-ingredient-block">
                          {product?.ingredients?.map((ingredient) => (
                            <li key={ingredient.title}>
                              <img
                                src={ingredient?.iconImage?.url || ""}
                                alt={ingredient.title || ""}
                              />
                              <p>{ingredient.title}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 0 }}>
                  <RockstarRelatedProducts />
                </div>

                <div
                  className="locator-modal modal-block"
                  style={{ display: showNotAvailableModal ? "block" : "none" }}
                >
                  <div className="locator-modal-wrapper">
                    <div className="locator-modal-details">
                      <a
                        href="#"
                        title=""
                        onClick={() => setShowNotAvailableModal(false)}
                        className="locator-modal-close"
                      >
                        <svg
                          data-v-750c1200=""
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 55.8 58"
                        >
                          <path
                            data-v-750c1200=""
                            fill="var(--global--color-logo)"
                            d="M55.8 3.5L52.2 0 27.9 24.4 3.5 0 0 3.5l24.4 24.4L0 52.2l3.5 3.6 24.4-24.4 24.3 24.4 3.6-3.6-24.4-24.3z"
                          ></path>
                        </svg>
                      </a>
                      <iframe src={storeLocatorLink} frameBorder="0"></iframe>
                    </div>
                  </div>
                </div>
              </article>
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
      {lightboxImage?.isOpen && (
        <Lightbox
          mainSrc={images?.[lightboxImage?.activeIdx || 0] || ""}
          nextSrc={
            images?.[(lightboxImage?.activeIdx || 0 + 1) % images.length]
          }
          prevSrc={
            images?.[
              (lightboxImage?.activeIdx || 0 + images.length - 1) %
                images.length
            ]
          }
          onCloseRequest={() =>
            setLightboxImage((prev) => ({ ...prev, isOpen: false }))
          }
          onMovePrevRequest={() =>
            setLightboxImage((prev) => ({
              ...prev,
              activeIdx:
                (prev?.activeIdx + (images || []).length - 1) %
                ((images || []).length || 1),
            }))
          }
          onMoveNextRequest={() =>
            setLightboxImage((prev) => ({
              ...prev,
              activeIdx: (prev?.activeIdx + 1) % ((images || []).length || 1),
            }))
          }
        />
      )}
    </>
  );
};

export default ProductPage;
